/**
 * Order Completion Manager
 * Form validation and auto-save functionality for order checkout
 *
 * Active functionality:
 * - Form validation before submission
 * - Delivery method/schedule stubs (placeholders)
 * - Auto-save form values to localStorage (user-specific via cookie, 7-day expiration)
 */

/**
 * Získá user hash z cookie
 * @returns {string|null} User hash nebo null pokud není přihlášen
 */
function getUserHash() {
    const name = 'reda_user_hash=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length, cookie.length);
        }
    }
    return null;
}

/**
 * Kontrola zda je uživatel přihlášen
 * @returns {boolean}
 */
function isUserLoggedIn() {
    return getUserHash() !== null;
}

/**
 * Form Storage Manager
 * Handles saving/loading form data to/from localStorage
 * Uses reda_user_hash cookie for user identification
 */
class FormStorageManager {
    constructor(config = {}) {
        this.config = {
            storageKey: 'drmax_order_form',
            expirationDays: 7,
            debug: false,
            ...config
        };

        // Fields to save/load (IDs from page-objednavka-dokonceni-obj.tpl)
        this.fieldsToSave = [
            'fullname',       // jméno
            'email_contact',  // e-mail
            'mobile_contact', // telefon
            'notice_usr',     // poznámka
            'id_plt_podm',    // platba
            'id_dod_podm',    // doprava
            'street',         // ulice (adresa)
            'city',           // město (adresa)
            'zip-code'        // PSČ (adresa)
            // 'country' je disabled, neukládáme
        ];

        // Custom address fields no longer used (fields are always visible in new template)
        this.customAddressFields = [];
    }

    /**
     * Get storage key with user hash
     */
    getStorageKey() {
        const userHash = getUserHash();
        if (!userHash) {
            this._log('No user hash available');
            return null;
        }
        return `${this.config.storageKey}_${userHash}`;
    }

    /**
     * Check if stored data is expired
     */
    isExpired(timestamp) {
        if (!timestamp) return true;

        const expirationMs = this.config.expirationDays * 24 * 60 * 60 * 1000;
        return (Date.now() - timestamp) > expirationMs;
    }

    /**
     * Check if field should be loaded from storage
     * Don't load if field already has a value from backend
     */
    shouldLoadField(fieldId, currentValue) {
        // If field has non-empty value from backend, don't overwrite
        if (currentValue && currentValue.trim() !== '' && currentValue !== 'NOT') {
            this._log(`Field ${fieldId} has backend value, skipping load`);
            return false;
        }
        return true;
    }

    /**
     * Save form data to localStorage
     */
    save(formElement) {
        if (!isUserLoggedIn()) {
            this._log('User not logged in, skipping save');
            return false;
        }

        const storageKey = this.getStorageKey();
        if (!storageKey) {
            return false;
        }

        const userHash = getUserHash();
        const data = {
            timestamp: Date.now(),
            userHash: userHash,
            fields: {}
        };

        // Save standard fields
        this.fieldsToSave.forEach(fieldId => {
            const field = formElement.elements[fieldId];
            if (field && field.value) {
                data.fields[fieldId] = field.value;
            }
        });

        // Save custom address fields if visible
        const customAddressDiv = document.getElementById('otheradress');
        if (customAddressDiv && customAddressDiv.style.display !== 'none') {
            data.fields.customAddress = {};
            this.customAddressFields.forEach(fieldId => {
                const field = document.getElementById(fieldId);
                if (field && field.value) {
                    data.fields.customAddress[fieldId] = field.value;
                }
            });
        }

        try {
            localStorage.setItem(storageKey, JSON.stringify(data));
            this._log('Form data saved', data);
            return true;
        } catch (error) {
            console.error('[FormStorage] Save failed:', error);
            return false;
        }
    }

    /**
     * Load form data from localStorage
     */
    load(formElement) {
        if (!isUserLoggedIn()) {
            this._log('User not logged in, skipping load');
            return false;
        }

        const storageKey = this.getStorageKey();
        if (!storageKey) {
            return false;
        }

        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) {
                this._log('No stored data found');
                return false;
            }

            const data = JSON.parse(stored);

            // Check expiration
            if (this.isExpired(data.timestamp)) {
                this._log('Stored data expired, clearing');
                this.clear();
                return false;
            }

            // Check user hash match (security)
            const currentHash = getUserHash();
            if (data.userHash !== currentHash) {
                this._log('User hash mismatch, skipping load');
                return false;
            }

            let loadedCount = 0;

            // Load standard fields
            this.fieldsToSave.forEach(fieldId => {
                if (data.fields[fieldId]) {
                    const field = formElement.elements[fieldId];
                    if (field && this.shouldLoadField(fieldId, field.value)) {
                        field.value = data.fields[fieldId];
                        loadedCount++;
                        this._log(`Loaded ${fieldId}:`, data.fields[fieldId]);
                    }
                }
            });

            // Load custom address fields if present
            if (data.fields.customAddress) {
                this.customAddressFields.forEach(fieldId => {
                    if (data.fields.customAddress[fieldId]) {
                        const field = document.getElementById(fieldId);
                        if (field && this.shouldLoadField(fieldId, field.value)) {
                            field.value = data.fields.customAddress[fieldId];
                            loadedCount++;
                            this._log(`Loaded custom address ${fieldId}:`, data.fields.customAddress[fieldId]);
                        }
                    }
                });
            }

            this._log(`Loaded ${loadedCount} fields from storage`);
            return loadedCount > 0;

        } catch (error) {
            console.error('[FormStorage] Load failed:', error);
            return false;
        }
    }

    /**
     * Clear stored form data
     */
    clear() {
        const storageKey = this.getStorageKey();
        if (!storageKey) {
            return false;
        }

        try {
            localStorage.removeItem(storageKey);
            this._log('Storage cleared');
            return true;
        } catch (error) {
            console.error('[FormStorage] Clear failed:', error);
            return false;
        }
    }

    /**
     * Debug logging
     */
    _log(...args) {
        if (this.config.debug) {
            console.log('[FormStorage]', ...args);
        }
    }
}

/**
 * Debounce utility
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Order Completion Manager
 */
class OrderCompletionManager {
    constructor(config = {}) {
        this.config = {
            debug: false,
            autosaveDelay: 1000, // 1 second debounce
            messages: {
                addressNotSelected: 'Prosím vyberte dodací adresu'
            },
            ...config
        };

        this.form = null;
        this.storageManager = null;

        this.init();
    }

    init() {
        this._log('Initializing order completion manager');

        this.form = document.getElementById('objednavka-form');
        if (!this.form) {
            this._log('Order completion form not found');
            return;
        }

        this.setupFormValidation();
        this.setupDeliveryOptions();
        this.setupLegacyFunctions();
        this.setupFormStorage();
    }

    // === Form Storage ===

    setupFormStorage() {
        if (!isUserLoggedIn()) {
            this._log('User not logged in, form storage disabled');
            return;
        }

        const userHash = getUserHash();
        this._log('User logged in, hash:', userHash);

        this.storageManager = new FormStorageManager({
            debug: this.config.debug
        });

        // Load saved data
        this.storageManager.load(this.form);

        // Setup auto-save on field changes
        this.setupAutoSave();

        this._log('Form storage initialized');
    }

    setupAutoSave() {
        if (!this.storageManager) return;

        // Create debounced save function (for typing)
        const debouncedSave = debounce(() => {
            this.storageManager.save(this.form);
        }, this.config.autosaveDelay);

        // Create immediate save function (for blur and page unload)
        const immediateSave = () => {
            this.storageManager.save(this.form);
        };

        // Get all form fields
        const fields = this.form.querySelectorAll('input, select, textarea');

        fields.forEach(field => {
            // Skip submit buttons and hidden fields
            if (field.type === 'submit' || field.type === 'button' || field.type === 'hidden') {
                return;
            }

            // Listen for changes (debounced)
            field.addEventListener('change', () => {
                this._log('Field changed, scheduling save:', field.id || field.name);
                debouncedSave();
            });

            // Also listen for input events on text fields (debounced)
            if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
                field.addEventListener('input', () => {
                    debouncedSave();
                });
            }

            // Save immediately when field loses focus (blur)
            field.addEventListener('blur', () => {
                this._log('Field blurred, saving immediately:', field.id || field.name);
                immediateSave();
            });
        });

        // Save before leaving the page
        window.addEventListener('beforeunload', () => {
            this._log('Page unload, saving immediately');
            immediateSave();
        });

        this._log('Auto-save enabled for', fields.length, 'fields (change, input, blur + beforeunload)');
    }

    // === Form Validation ===

    setupFormValidation() {
        if (!this.form) return;

        this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
        this._log('Form validation initialized');
    }

    handleFormSubmit(event) {
        const form = event.target;
        const addressSelect = form.elements['id_provozov'];

        if (!addressSelect || addressSelect.value === 'NOT') {
            event.preventDefault();

            const errorMsg = window.orderData?.messages?.addressNotSelected ||
                            this.config.messages.addressNotSelected;
            alert(errorMsg);

            this._log('Form validation failed: No address selected');
            return false;
        }

        // Clear storage on successful submission
        if (this.storageManager) {
            this._log('Form submitted, clearing storage');
            this.storageManager.clear();
        }

        this._log('Form validation passed');
        return true;
    }

    // === Delivery Options ===

    setupDeliveryOptions() {
        // Delivery method select
        const deliverySelect = document.getElementById('id_dod_podm');
        if (deliverySelect) {
            deliverySelect.addEventListener('change', (e) => {
                this.handleDeliveryMethodChange(e.target.value);
            });
        }

        // Delivery schedule link
        const scheduleLink = document.getElementById('delivery-schedule-link');
        if (scheduleLink) {
            scheduleLink.addEventListener('click', (e) => {
                e.preventDefault();
                this.showDeliverySchedule();
            });
        }
    }

    handleDeliveryMethodChange(value) {
        this._log('Delivery method changed:', value);
        // TODO: Implement when backend provides functionality
        console.warn('[Order Completion] nastavZpusobDoprav() not implemented, value:', value);
    }

    showDeliverySchedule() {
        this._log('Show delivery schedule requested');
        // TODO: Implement delivery schedule modal/popup
        console.warn('[Order Completion] zobrazHarmonogramRozvozu() not implemented');
        return false;
    }

    // === Legacy Function Exposure ===

    setupLegacyFunctions() {
        // Expose functions globally for potential template compatibility
        window.nastavZpusobDoprav = (value) => this.handleDeliveryMethodChange(value);
        window.zobrazHarmonogramRozvozu = () => this.showDeliverySchedule();
    }

    // === Utilities ===

    _log(...args) {
        if (this.config.debug) {
            console.log('[OrderCompletion]', ...args);
        }
    }
}

// Initialize on DOM ready (conditional - only on order completion page)
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (document.getElementById('objednavka-form')) {
            window.orderCompletion = new OrderCompletionManager({
                debug: import.meta.env.DEV  // true in dev, false in production
            });
        }
    });
} else {
    if (document.getElementById('objednavka-form')) {
        window.orderCompletion = new OrderCompletionManager({
            debug: import.meta.env.DEV  // true in dev, false in production
        });
    }
}

export default OrderCompletionManager;
